import { formatCodeableConcept, type WithId } from '@medplum/core';
import type { Encounter } from '@medplum/fhirtypes';
import { ExternalLinkIcon, PanelBottomOpenIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { formatDateTime } from '../../utils/date';
import { Button } from '../ui/button';
import { Item, ItemContent, ItemHeader } from '../ui/item';
import { Skeleton } from '../ui/skeleton';

export type ProcedureLinkedEncounterProps = {
  /** The resolved `Procedure.encounter`. Absent renders nothing (unless still loading). */
  encounter: WithId<Encounter> | undefined;
  isLoading?: boolean;
  /** Navigate to the encounter in the Data tab. Omit to hide the link. */
  onSelectEncounter?: (encounterId: string) => void;
  /** Open the encounter in the shared drawer. Omit to hide the button. */
  onOpenEncounterInDrawer?: (encounterId: string) => void;
};

/**
 * The linked-visit block on a procedure detail. Renders the encounter's type, date and location
 * alongside the two affordances for reaching it — navigating the Data tab to it, and opening it
 * in the shared drawer.
 *
 * Each affordance is gated on its own callback, so a consumer with no drawer to open into
 * (Storybook, the patient-facing surfaces) simply gets the link.
 */
export const ProcedureLinkedEncounter = ({
  encounter,
  isLoading = false,
  onSelectEncounter,
  onOpenEncounterInDrawer,
}: ProcedureLinkedEncounterProps): React.ReactElement | null => {
  const { t } = useTranslation('records');

  if (isLoading) {
    return (
      <Item>
        <ItemHeader>
          <ItemContent>
            <h2 className="text-lg font-semibold">{t('linkedVisit')}</h2>
            <Skeleton className="mt-2 h-5 w-64" />
          </ItemContent>
        </ItemHeader>
      </Item>
    );
  }

  if (!encounter) return null;

  const type = formatCodeableConcept(encounter.type?.at(0)) || t('unknownEncounter');
  const date = encounter.period?.start;
  const location = encounter.location?.at(0)?.location?.display;
  const subtitle = [date && formatDateTime(date), location].filter(Boolean).join(' · ');

  return (
    <Item data-testid="procedure-linked-encounter">
      <ItemHeader>
        <ItemContent>
          <h2 className="text-lg font-semibold">{t('linkedVisit')}</h2>
          <p className="mt-2">{type}</p>
          {subtitle && <p className="text-sm text-muted-foreground">{subtitle}</p>}
          <div className="mt-3 flex flex-wrap gap-2">
            {onSelectEncounter && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => onSelectEncounter(encounter.id)}
                data-testid="procedure-encounter-view-link"
              >
                <ExternalLinkIcon className="size-4" />
                {t('viewEncounter')}
              </Button>
            )}
            {onOpenEncounterInDrawer && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => onOpenEncounterInDrawer(encounter.id)}
                data-testid="procedure-encounter-drawer-button"
              >
                <PanelBottomOpenIcon className="size-4" />
                {t('openEncounterInDrawer')}
              </Button>
            )}
          </div>
        </ItemContent>
      </ItemHeader>
    </Item>
  );
};
